import React from "react";
import styled, { css, keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import { fonts } from "../../utils/theme";
import type { SupportStatus } from "../../utils/tickets";

/**
 * The support pages' visual register from the design handoff: glass cards
 * with a 1px border, caps eyebrows, dot + label statuses, text buttons for
 * secondary actions and one filled primary per footer.
 */
export const sp = {
    ink: "#0F1C4D",
    ink2: "#383F54",
    muted: "#6A7080",
    faint: "#9CA1AC",
    accent: "#276EF1",
    accentWash: "#EBF1F7",
    border: "#EBF1F7",
    borderStrong: "#D3E0EF",
    surface: "#FFFFFF",
    surface2: "#FAFAFA",
    sunken: "#F2F3F5",
    pos: "#136B4F",
    posWash: "#E9F3EE",
    neg: "#BB3A32",
    wash1: "#DEE9FB",
    display: fonts.tertiary,
    body: fonts.primary,
    easeOut: "cubic-bezier(.32,.72,0,1)",
    easeIn: "cubic-bezier(.4,0,.9,.3)",
    dIn: "380ms",
    dOut: "270ms",
    t: "150ms",
};

export const reducedMotion = (rules: ReturnType<typeof css> | string) => css`
    @media (prefers-reduced-motion: reduce) {
        ${rules}
    }
`;

export const riseIn = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: none; }
`;

export const GlassCard = styled.div<{ $pad?: boolean }>`
    background: color-mix(in srgb, ${sp.surface} 78%, transparent);
    border: 1px solid ${sp.border};
    border-radius: 14px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(18px) saturate(1.4);
    -webkit-backdrop-filter: blur(18px) saturate(1.4);
    ${({ $pad }) => $pad && "padding: 24px;"}

    @media (prefers-reduced-transparency: reduce) {
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        background: ${sp.surface};
    }
`;

export const Eyebrow = styled.span`
    font-family: ${sp.body};
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${sp.muted};
`;

export const statusTone = (status: SupportStatus) =>
    status === "done" ? sp.pos : status === "progress" ? sp.accent : sp.muted;

export const StatusLabel = styled.span<{ $color: string; $size?: number }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: ${sp.body};
    font-size: ${({ $size }) => $size ?? 12}px;
    font-weight: 600;
    white-space: nowrap;
    color: ${({ $color }) => $color};

    i {
        width: 6px;
        height: 6px;
        border-radius: 999px;
        background: currentColor;
        flex-shrink: 0;
    }
`;

/** Dot + label, never a pill: grey received, blue in progress, green resolved. */
export const StatusDot: React.FC<{ status: SupportStatus; size?: number; className?: string }> = ({
    status,
    size,
    className,
}) => {
    const { t } = useTranslation();
    return (
        <StatusLabel $color={statusTone(status)} $size={size} className={className}>
            <i />
            {t(`pages.requests.customer_status.${status}`)}
        </StatusLabel>
    );
};

export const TextButton = styled.button<{ $muted?: boolean }>`
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 10px;
    border-radius: 9px;
    font-family: ${sp.body};
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    color: ${({ $muted }) => ($muted ? sp.muted : sp.accent)};
    transition: background ${sp.t};

    &:hover {
        background: ${sp.accentWash};
    }

    &:active {
        background: ${sp.surface2};
    }

    &:focus-visible {
        outline: 2px solid ${sp.accent};
        outline-offset: 1px;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

export const PrimaryButton = styled.button`
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 36px;
    padding: 0 16px;
    border-radius: 8px;
    background: ${sp.ink};
    color: #fff;
    font-family: ${sp.body};
    font-weight: 600;
    font-size: 13.5px;
    letter-spacing: -0.005em;
    white-space: nowrap;
    box-shadow: 0 1px 2px rgba(15, 28, 77, 0.05);
    transition: background ${sp.t}, opacity ${sp.t}, box-shadow ${sp.t};

    &:hover {
        background: color-mix(in srgb, ${sp.ink} 84%, #fff);
    }

    &:disabled {
        opacity: 0.35;
        cursor: default;
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px color-mix(in srgb, ${sp.accent} 45%, transparent);
    }
`;

// The design's in-app ghost button is a text button in accent, not an outlined one.
export const GhostButton = styled(PrimaryButton)`
    height: 32px;
    padding: 0 10px;
    background: transparent;
    color: ${sp.accent};
    box-shadow: none;

    &:hover {
        background: ${sp.accentWash};
    }
`;

export const Segmented = styled.div`
    display: inline-flex;
    gap: 2px;
    height: 36px;
    padding: 3px;
    border-radius: 10px;
    background: ${sp.sunken};
    flex-shrink: 0;

    button {
        height: 30px;
        border: none;
        border-radius: 7px;
        background: none;
        padding: 0 11px;
        font-family: ${sp.body};
        font-size: 12.5px;
        font-weight: 600;
        color: ${sp.muted};
        cursor: pointer;
        transition: color ${sp.t}, background ${sp.t}, box-shadow ${sp.t};
    }

    button.on {
        background: ${sp.surface};
        color: ${sp.ink};
        box-shadow: 0 1px 3px rgba(15, 28, 77, 0.1), 0 1px 2px rgba(15, 28, 77, 0.06);
    }

    button:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px color-mix(in srgb, ${sp.accent} 45%, transparent);
    }

    .n {
        margin-left: 5px;
        color: ${sp.faint};
        font-weight: 600;
        font-variant-numeric: tabular-nums;
    }

    button.on .n {
        color: ${sp.muted};
    }
`;

/** Pill chips under the question box. */
export const PillChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    button {
        all: unset;
        box-sizing: border-box;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        height: 34px;
        padding: 0 13px 0 10px;
        border-radius: 999px;
        background: ${sp.surface};
        border: 1px solid ${sp.border};
        font-family: ${sp.body};
        font-size: 12.5px;
        font-weight: 600;
        color: ${sp.ink2};
        transition: background ${sp.t}, border-color ${sp.t}, color ${sp.t};

        &:hover {
            background: ${sp.accentWash};
            border-color: transparent;
            color: ${sp.ink};
        }

        &:active {
            background: ${sp.surface2};
        }
    }

    button.more {
        background: transparent;
        border-color: transparent;
        color: ${sp.muted};
        padding-right: 8px;

        &:hover {
            background: ${sp.surface2};
            color: ${sp.ink};
        }
    }
`;

/** Selectable chips in the new request sheet; the chosen one turns ink. */
export const SelectChips = styled.div<{ $bad?: boolean }>`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    ${({ $bad }) =>
        $bad &&
        css`
            padding: 8px;
            margin: -8px;
            border-radius: 14px;
            box-shadow: inset 0 0 0 1px color-mix(in srgb, ${sp.neg} 45%, transparent);
            background: color-mix(in srgb, ${sp.neg} 4%, transparent);
        `}

    button {
        all: unset;
        box-sizing: border-box;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        height: 36px;
        padding: 0 13px;
        border-radius: 10px;
        border: 1px solid ${sp.border};
        background: ${sp.surface};
        font-family: ${sp.body};
        font-size: 13px;
        font-weight: 600;
        color: ${sp.ink2};
        transition: background ${sp.t}, border-color ${sp.t}, color ${sp.t};

        small {
            font-weight: 500;
            color: ${sp.muted};
        }

        &:hover {
            background: ${sp.surface2};
        }

        &.on {
            background: ${sp.ink};
            border-color: ${sp.ink};
            color: #fff;

            small {
                color: rgba(255, 255, 255, 0.8);
            }
        }
    }
`;

/** Tinted icon disc; filled accent when something new waits in the row. */
export const IconDisc = styled.span<{ $size?: number; $filled?: boolean }>`
    width: ${({ $size }) => $size ?? 34}px;
    height: ${({ $size }) => $size ?? 34}px;
    border-radius: 999px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ $filled }) =>
        $filled
            ? sp.accent
            : `linear-gradient(160deg, color-mix(in srgb, ${sp.wash1} 90%, ${sp.surface}), ${sp.accentWash})`};
    color: ${({ $filled }) => ($filled ? "#fff" : sp.accent)};
    box-shadow: ${({ $filled }) => ($filled ? "none" : "inset 0 1px 0 rgba(255,255,255,.8)")};
`;
